/**
 * @fileoverview Compose / reply dialog for a job mailbox (MOR-529).
 *
 * Human-authored sends go straight out through `composeUserEmail` (the human
 * is the approver — no draft queue here). To/Cc/Bcc are comma-separated
 * inputs parsed into arrays; the backend re-validates every address.
 */

import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { extractErrorMessage } from "@/utils/errors";
import {
  AttachmentChipList,
  MailAttachmentPicker,
} from "./MailAttachmentPicker";
import { useDraftAutosave } from "./useDraftAutosave";
import type { ComposerAttachment } from "./types";

/** Optional prefill for reply flows. */
export interface ComposePrefill {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  bodyText?: string;
  attachments?: ComposerAttachment[];
  inReplyToInkboxMessageId?: string;
}

interface ComposeDialogProps {
  jobId: Id<"jobs">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: ComposePrefill;
}

/** Split a comma-separated address input into a trimmed, non-empty array. */
function parseAddresses(input: string): string[] {
  return input
    .split(",")
    .map((a) => a.trim())
    .filter((a) => a.length > 0);
}

export function ComposeDialog({
  jobId,
  open,
  onOpenChange,
  prefill,
}: ComposeDialogProps) {
  const composeUserEmail = useMutation(api.mail.mutations.composeUserEmail);

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [dialogAttachments, setDialogAttachments] = useState<
    ComposerAttachment[]
  >([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Forward drafts autosave as plain compose drafts (no threadId); they
  // reopen in the full composer via the Drafts folder. The parent mounts
  // this dialog only while open, so unmount-on-close flushes the last save.
  const { flush, release } = useDraftAutosave({
    jobId,
    enabled: open && !sending,
    payload: {
      to: parseAddresses(to),
      cc: parseAddresses(cc),
      bcc: parseAddresses(bcc),
      subject,
      bodyText: body,
      attachments: dialogAttachments,
    },
  });

  // Re-seed the form whenever the dialog opens (so a reply prefills fresh and
  // a later plain compose starts blank).
  useEffect(() => {
    if (!open) return;
    setTo((prefill?.to ?? []).join(", "));
    setCc((prefill?.cc ?? []).join(", "));
    setBcc((prefill?.bcc ?? []).join(", "));
    setSubject(prefill?.subject ?? "");
    setBody(prefill?.bodyText ?? "");
    setDialogAttachments(prefill?.attachments ?? []);
    setError(null);
  }, [open, prefill]);

  const handleSend = async () => {
    setError(null);
    const toList = parseAddresses(to);
    const ccList = parseAddresses(cc);
    const bccList = parseAddresses(bcc);
    if (toList.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    if (subject.trim().length === 0) {
      setError("Subject is required.");
      return;
    }
    if (body.trim().length === 0) {
      setError("Message body is required.");
      return;
    }

    setSending(true);
    try {
      // Save first so the send adopts this draft row — a rejected send then
      // reappears in Drafts (with the error) instead of vanishing.
      const draftId = await flush();
      await composeUserEmail({
        jobId,
        draftId: draftId ?? undefined,
        to: toList,
        cc: ccList.length > 0 ? ccList : undefined,
        bcc: bccList.length > 0 ? bccList : undefined,
        subject,
        bodyText: body,
        inReplyToInkboxMessageId: prefill?.inReplyToInkboxMessageId,
        attachments:
          dialogAttachments.length > 0
            ? dialogAttachments.map((a) => ({
                filename: a.filename,
                contentType: a.contentType,
                storageId: a.storageId,
                documentId: a.documentId,
              }))
            : undefined,
      });
      release();
      toast.success("Sending...");
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to send message"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {prefill?.inReplyToInkboxMessageId ? "Reply" : "New message"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="mail-to">To</Label>
            <Input
              id="mail-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="name@example.com, other@example.com"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mail-cc">Cc</Label>
            <Input
              id="mail-cc"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="Optional, comma-separated"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mail-bcc">Bcc</Label>
            <Input
              id="mail-bcc"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder="Optional, comma-separated"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mail-subject">Subject</Label>
            <Input
              id="mail-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mail-body">Message</Label>
            <Textarea
              id="mail-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="resize-y"
            />
          </div>
          <AttachmentChipList
            jobId={jobId}
            attachments={dialogAttachments}
            onRemove={(index) =>
              setDialogAttachments(
                dialogAttachments.filter((_, i) => i !== index),
              )
            }
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <MailAttachmentPicker
            jobId={jobId}
            attachments={dialogAttachments}
            onChange={setDialogAttachments}
            disabled={sending}
          />
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending}>
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
